import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RefreshCw, ArrowLeft } from 'lucide-react';
import type { RootState } from '@/state/store';
import { useSelectedComponent } from '@/hooks/useSelectedComponent';
import ComponentNotFound from '@/hooks/ComponentNotFound';
import OnboardingPageHeader from '@/components/smartai/ComponentOnboarding/OnboardingPageHeader';
import OnboardingProjectHeader from '@/components/smartai/ComponentOnboarding/OnboardingProjectHeader';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { CoverageHistoryMode } from '@/components/smartai/api/getCoverageHistory';
import { useCoverageHistoryTrendRuns } from '@/components/smartai/api/getCoverageHistory';
import {
  compareFromTrendPoints,
  formatProcessedAt,
  HISTORY_MODE_LABELS,
  pctFromSummary,
  type CoverageChartMetric,
} from './coverage-history-utils';
import {
  coverageCardClass,
  coveragePageClass,
  CoverageStateMessage,
} from './coverage-dashboard-layout';
import {
  TrendSection,
  TrendSegmentGroup,
} from './coverage-trend-layout';
import CoverageTrendDashboardMetrics from './CoverageTrendDashboardMetrics';
import CoverageByBuildChart from './CoverageByBuildChart';
import CoverageTrendChart from './CoverageTrendChart';
import CoverageHistoryRunsTable from './CoverageHistoryRunsTable';
import CoverageDashboardSkeleton from './CoverageDashboardSkeleton';

const MODE_OPTIONS: CoverageHistoryMode[] = [
  'combined',
  'automation',
  'manual',
];

function parseMode(raw: string | null): CoverageHistoryMode {
  if (raw === 'automation' || raw === 'manual' || raw === 'combined') {
    return raw;
  }
  return 'combined';
}

function runKey(point: { runId?: string | null; processedAt?: string }) {
  return point.runId ?? point.processedAt ?? '';
}

function compareAtIndex(
  points: ReturnType<typeof useCoverageHistoryTrendRuns>['data'],
  index: number,
) {
  if (!points?.length || index < 0 || index >= points.length) {
    return compareFromTrendPoints([]);
  }
  const slice = points.slice(0, index + 1);
  return compareFromTrendPoints(slice);
}

const ComponentCoverageBuildRunsView = () => {
  const { scanId: scanIdParam } = useParams<{ scanId: string }>();
  const scanId = (scanIdParam ?? '').trim();
  const { ready, notFound, isLoading, dbRepoId } = useSelectedComponent();
  const selected = useSelector(
    (state: RootState) => state.userSelectedComponentSlice,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [chartMetric, setChartMetric] = useState<CoverageChartMetric>('both');
  const [selectedRunKey, setSelectedRunKey] = useState('');

  const mode = parseMode(searchParams.get('bucket'));

  const setMode = (next: CoverageHistoryMode) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('bucket', next);
    setSearchParams(nextParams, { replace: true });
  };

  const repoId = selected.dbRepoId ?? dbRepoId ?? '';
  const runsQuery = useCoverageHistoryTrendRuns(scanId, mode);
  const points = runsQuery.data ?? [];

  useEffect(() => {
    if (!points.length) return;
    const keys = points.map(runKey);
    if (!selectedRunKey || !keys.includes(selectedRunKey)) {
      setSelectedRunKey(keys[keys.length - 1] ?? '');
    }
  }, [points, selectedRunKey]);

  const selectedIndex = useMemo(() => {
    if (!selectedRunKey) return points.length - 1;
    const idx = points.findIndex(p => runKey(p) === selectedRunKey);
    return idx >= 0 ? idx : points.length - 1;
  }, [points, selectedRunKey]);

  const compare = useMemo(
    () => compareAtIndex(points, selectedIndex),
    [points, selectedIndex],
  );

  const headerBuild = points[0]?.buildNumber ?? points[0]?.buildId;
  const headerScan = points[0]?.scanNumber;

  const handleRefresh = () => {
    void runsQuery.refetch();
  };

  const trendBranchPath = `/sia/components/coverage_analysis/trend/${repoId}?bucket=${mode}`;

  if (notFound) {
    return <ComponentNotFound />;
  }

  if (isLoading || !ready) {
    return (
      <div className={coveragePageClass}>
        <CoverageDashboardSkeleton />
      </div>
    );
  }

  return (
    <div className={coveragePageClass}>
      <OnboardingPageHeader
        eyebrow="Coverage Analysis"
        title="Coverage runs"
        subtitle="Executions within one build — aggregated from coverage timeline file data."
      />

      <OnboardingProjectHeader
        org={selected.orgName}
        project={selected.projectName || '—'}
        repoName={selected.repoName || '—'}
        branchName={
          (selected.branchName || 'main').replace(/^refs\/heads\//, '') || '—'
        }
      />

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" size="sm" asChild className="h-8 gap-1.5 rounded-lg">
          <Link to={trendBranchPath}>
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
            Branch trend
          </Link>
        </Button>
        {scanId ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 rounded-lg"
            onClick={handleRefresh}
            disabled={runsQuery.isFetching}>
            <RefreshCw
              className={cn(
                'h-3.5 w-3.5',
                runsQuery.isFetching && 'animate-spin',
              )}
              aria-hidden
            />
            Refresh
          </Button>
        ) : null}
      </div>

      <section className={cn(coverageCardClass, 'space-y-6')}>
        <div className="flex flex-col gap-4 border-b border-[var(--inv-border-soft)] pb-5 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <div className="min-w-0 max-w-xl">
            <h3 className="m-0 font-serif text-[1rem] font-semibold text-[var(--inv-text)]">
              Build
              {headerBuild != null ? ` ${headerBuild}` : ''}
              {headerScan != null ? ` · Scan #${headerScan}` : ''}
            </h3>
            <p className="m-0 mt-1 font-mono text-[0.75rem] text-[var(--inv-text-secondary)]">
              {scanId || '—'}
            </p>
            <p className="m-0 mt-1 text-[0.8125rem] leading-relaxed text-[var(--inv-text-secondary)]">
              Each run is a coverage rollup on this scan. Legacy rows without a
              run id are grouped by processed time.
            </p>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:gap-6">
            <TrendSegmentGroup
              label="Coverage source"
              options={MODE_OPTIONS.map(m => ({
                value: m,
                label: HISTORY_MODE_LABELS[m],
              }))}
              value={mode}
              onChange={setMode}
            />
            <TrendSegmentGroup
              label="LOC / inventory"
              ariaLabel="Lines of code or inventory measure"
              options={[
                { value: 'both', label: 'Both' },
                { value: 'lines', label: 'Lines' },
                { value: 'inventory', label: 'Inventory' },
              ]}
              value={chartMetric}
              onChange={setChartMetric}
            />
          </div>
        </div>

        {!scanId ? (
          <CoverageStateMessage variant="empty">
            Scan id is missing from the URL.
          </CoverageStateMessage>
        ) : null}

        {runsQuery.isPending && scanId ? <CoverageDashboardSkeleton /> : null}

        {runsQuery.isError ? (
          <CoverageStateMessage variant="error">
            Failed to load runs: {(runsQuery.error as Error)?.message}
          </CoverageStateMessage>
        ) : null}

        {scanId && !runsQuery.isPending && !runsQuery.isError ? (
          <div className="space-y-8">
            {points.length > 1 ? (
              <div className="flex flex-col gap-2 sm:max-w-md">
                <label
                  htmlFor="coverage-run-select"
                  className="text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-[var(--inv-text-muted)]">
                  Selected run
                </label>
                <select
                  id="coverage-run-select"
                  className="h-9 rounded-lg border border-[var(--inv-border)] bg-[var(--inv-surface)] px-3 text-[0.8125rem] text-[var(--inv-text)]"
                  value={selectedRunKey}
                  onChange={e => setSelectedRunKey(e.target.value)}>
                  {[...points].reverse().map(p => {
                    const key = runKey(p);
                    const label =
                      p.runNumber != null
                        ? `Run ${p.runNumber}`
                        : formatProcessedAt(p.processedAt);
                    const loc = pctFromSummary(p.summary, 'lines');
                    const suffix =
                      loc != null ? ` · ${loc.toFixed(1)}% LOC` : '';
                    return (
                      <option key={key} value={key}>
                        {label}
                        {p.sourceType ? ` (${p.sourceType})` : ''}
                        {suffix}
                      </option>
                    );
                  })}
                </select>
              </div>
            ) : null}

            <TrendSection
              title="Run snapshot"
              description={
                compare.latest?.runNumber != null
                  ? `Run ${compare.latest.runNumber} — deltas vs. the previous run on this build.`
                  : 'Deltas compare the selected run to the previous execution.'
              }>
              <CoverageTrendDashboardMetrics
                mode={mode}
                metricKind={chartMetric}
                latest={compare.latest}
                previous={compare.previous}
              />
            </TrendSection>

            <CoverageByBuildChart
              points={points}
              metric={chartMetric}
              axisVariant="run"
            />

            <TrendSection
              title="Coverage over runs"
              description="Trend line per execution; follows LOC / inventory above.">
              <CoverageTrendChart points={points} metric={chartMetric} />
            </TrendSection>

            <TrendSection
              title="Run history"
              description="Newest runs first.">
              {points.length ? (
                <CoverageHistoryRunsTable points={points} variant="run" />
              ) : (
                <CoverageStateMessage variant="empty">
                  No coverage timeline runs for this scan yet.
                </CoverageStateMessage>
              )}
            </TrendSection>
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default ComponentCoverageBuildRunsView;
